import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Eye, EyeSlash, MagnifyingGlass, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";

const STATE_META = {
  on: { label: "On Board", bg: "#22c55e" },
  hidden: { label: "Hidden", bg: "#ef4444" },
  off_no_upload: { label: "Off (no upload)", bg: "#fbbf24" },
};
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function RaceManageDialog({ trigger, year, onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("on"); // "on" | "off"
  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/race/manage?year=${year}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onRows = useMemo(() => (data?.rows || []).filter((r) => r.state === "on"), [data]);
  const offRows = useMemo(
    () => (data?.rows || []).filter((r) => r.state === "hidden" || r.state === "off_no_upload"),
    [data],
  );
  const visible = (tab === "on" ? onRows : offRows).filter(
    (r) => !q || r.agent_name.toLowerCase().includes(q.toLowerCase()),
  );

  const refreshAndNotify = async () => {
    await load();
    onChanged?.();
  };

  const hideAgent = async (row) => {
    try {
      await api.post("/race/hide", { agent_key: row.agent_key, year });
      toast.success(`${row.agent_name} hidden`);
      refreshAndNotify();
    } catch (err) { toast.error(err.response?.data?.detail || "Hide failed"); }
  };
  const showAgent = async (row) => {
    try {
      if (row.state === "hidden") {
        await api.post("/race/unhide", { agent_key: row.agent_key, year });
      } else {
        await api.post("/race/show", { agent_key: row.agent_key, year });
      }
      toast.success(`${row.agent_name} restored`);
      refreshAndNotify();
    } catch (err) { toast.error(err.response?.data?.detail || "Restore failed"); }
  };

  const startEdit = (row) => {
    setEditKey(row.agent_key);
    setEditForm({
      life_dis_ltc_premium: row.life_dis_ltc_premium || 0,
      paid_cases: row.paid_cases || 0,
      total_gdc: row.total_gdc || 0,
      cfn_start_date: (row.cfn_start_date || "").slice(0, 10),
    });
  };
  const cancelEdit = () => { setEditKey(null); setEditForm({}); };
  const saveEdit = async (row) => {
    setSaving(true);
    try {
      const payload = {
        life_dis_ltc_premium: Number(editForm.life_dis_ltc_premium) || 0,
        paid_cases: Math.round(Number(editForm.paid_cases) || 0),
        total_gdc: Number(editForm.total_gdc) || 0,
      };
      if (editForm.cfn_start_date) payload.cfn_start_date = editForm.cfn_start_date;
      await api.patch(`/awards/leaders/data/${encodeURIComponent(row.agent_key)}?year=${year}`, payload);
      toast.success(`${row.agent_name} updated`);
      cancelEdit();
      refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">Manage Race Board · {year}</DialogTitle>
          <DialogDescription className="text-zinc-600">
            Show/hide agents and edit Tier 1 / Tier 2 numbers without re-uploading the whole spreadsheet.
            {data?.has_uploads ? " An upload exists for this year, so only uploaded agents appear by default." : " No upload yet — all registered agents appear by default."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="brut bg-white p-1 inline-flex" data-testid="manage-tab-toggle">
            <button onClick={() => setTab("on")} className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${tab === "on" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="manage-tab-on">
              On board · {onRows.length}
            </button>
            <button onClick={() => setTab("off")} className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${tab === "off" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="manage-tab-off">
              Off board · {offRows.length}
            </button>
          </div>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name…"
              className="pl-9 border-2 border-black rounded-none h-10 focus-visible:ring-0"
              data-testid="manage-search"
            />
          </div>
        </div>

        <div className="brut bg-white overflow-auto flex-1" data-testid="manage-list">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-[#09090b] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Agent</th>
                <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">State</th>
                <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Life/Dis/LTC $</th>
                <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Cases</th>
                <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Total GDC $</th>
                <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Start</th>
                <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const meta = STATE_META[r.state] || STATE_META.on;
                const editing = editKey === r.agent_key;
                return (
                  <tr key={r.agent_key} className="border-b border-zinc-200 last:border-b-0 hover:bg-[#fafafa]" data-testid={`manage-row-${r.agent_key}`}>
                    <td className="px-3 py-2 font-bold">
                      {r.agent_name}
                      {r.is_external && <span className="ml-2 inline-block border-2 border-black px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#fef3c7]">EXT</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 border-2 border-black text-[10px] font-black uppercase tracking-widest text-white" style={{ background: meta.bg }}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editing ? (
                        <Input type="number" min="0" step="0.01" value={editForm.life_dis_ltc_premium}
                          onChange={(e) => setEditForm({ ...editForm, life_dis_ltc_premium: e.target.value })}
                          className="h-8 border-2 border-black text-right w-32 rounded-none focus-visible:ring-0"
                          data-testid={`edit-premium-${r.agent_key}`} />
                      ) : fmt$(r.life_dis_ltc_premium)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editing ? (
                        <Input type="number" min="0" step="1" value={editForm.paid_cases}
                          onChange={(e) => setEditForm({ ...editForm, paid_cases: e.target.value })}
                          className="h-8 border-2 border-black text-right w-20 rounded-none focus-visible:ring-0"
                          data-testid={`edit-cases-${r.agent_key}`} />
                      ) : r.paid_cases}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editing ? (
                        <Input type="number" min="0" step="0.01" value={editForm.total_gdc}
                          onChange={(e) => setEditForm({ ...editForm, total_gdc: e.target.value })}
                          className="h-8 border-2 border-black text-right w-32 rounded-none focus-visible:ring-0"
                          data-testid={`edit-gdc-${r.agent_key}`} />
                      ) : fmt$(r.total_gdc)}
                    </td>
                    <td className="px-3 py-2">
                      {editing ? (
                        <input type="date" value={editForm.cfn_start_date}
                          onChange={(e) => setEditForm({ ...editForm, cfn_start_date: e.target.value })}
                          className="h-8 border-2 border-black px-2 w-36 text-sm focus:outline-none"
                          data-testid={`edit-start-${r.agent_key}`} />
                      ) : (r.cfn_start_date ? r.cfn_start_date.slice(0, 10) : <span className="text-zinc-400">—</span>)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {editing ? (
                          <>
                            <button onClick={() => saveEdit(r)} disabled={saving}
                              className="border-2 border-black p-1.5 hover:bg-[#22c55e] hover:text-white disabled:opacity-40"
                              title="Save" data-testid={`manage-save-${r.agent_key}`}>
                              <FloppyDisk size={12} weight="bold" />
                            </button>
                            <button onClick={cancelEdit}
                              className="border-2 border-black p-1.5 hover:bg-zinc-200"
                              title="Cancel" data-testid={`manage-cancel-${r.agent_key}`}>
                              <X size={12} weight="bold" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(r)}
                              className="border-2 border-black p-1.5 hover:bg-[#fef3c7]"
                              title="Edit numbers" data-testid={`manage-edit-${r.agent_key}`}>
                              <PencilSimple size={12} weight="bold" />
                            </button>
                            {r.state === "on" ? (
                              <button onClick={() => hideAgent(r)}
                                className="border-2 border-black p-1.5 hover:bg-[#fb7185] hover:text-white"
                                title="Hide from race" data-testid={`manage-hide-${r.agent_key}`}>
                                <EyeSlash size={12} weight="bold" />
                              </button>
                            ) : (
                              <button onClick={() => showAgent(r)}
                                className="border-2 border-black p-1.5 hover:bg-[#22c55e] hover:text-white"
                                title="Add to race" data-testid={`manage-show-${r.agent_key}`}>
                                <Eye size={12} weight="bold" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  {tab === "on" ? "No one on the board yet." : "Nobody off the board."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => setOpen(false)} className="rounded-none bg-white text-black border-2 border-black hover:bg-zinc-100 font-bold uppercase tracking-widest text-xs h-10">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
